import { PLANON_GENDER_OPTIONS, PLANON_PERSON_TYPE_OPTIONS, PLANON_ZONE_OPTIONS } from '@hcc/types';
import { MultiSelect } from '../../../components/ui/MultiSelect';
import { RefSelect } from './RefSelect';
import { PicklistSelect } from './PicklistSelect';
import { type PlanonDraft, FIELD_LABEL_CLS, FIELD_INPUT_CLS } from './usePlanonReference';

/** The editable Planon create fields. Identity is shown read-only by the modal. */
export function PlanonPersonForm({
  draft,
  onChange,
}: {
  draft: PlanonDraft;
  onChange: (next: PlanonDraft) => void;
}) {
  const set = <K extends keyof PlanonDraft>(key: K, value: PlanonDraft[K]) =>
    onChange({ ...draft, [key]: value });

  return (
    <div className="space-y-5">
      <fieldset className="space-y-3">
        <legend className="text-xs font-semibold uppercase tracking-wider text-fg-dim">
          Role &amp; org
        </legend>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <RefSelect kind="position" label="Position" value={draft.personPositionRef} onChange={(v) => set('personPositionRef', v)} />
          <RefSelect kind="department" label="Department" value={draft.departmentRef} onChange={(v) => set('departmentRef', v)} />
          <RefSelect kind="cost_centre" label="Cost centre" value={draft.costCentreRef} onChange={(v) => set('costCentreRef', v)} />
          <RefSelect kind="employment_type" label="Employment type" value={draft.employmentTypeRef} onChange={(v) => set('employmentTypeRef', v)} />
          <RefSelect kind="tariff_group" label="Tariff group" value={draft.tariffGroupRef} onChange={(v) => set('tariffGroupRef', v)} />
          <RefSelect kind="trade" label="Trade" value={draft.tradeSyscode} onChange={(v) => set('tradeSyscode', v)} />
          <RefSelect kind="property" label="Property" value={draft.propertyRef} onChange={(v) => set('propertyRef', v)} />
          <RefSelect kind="country" label="Country" value={draft.country2} onChange={(v) => set('country2', v)} />
          <RefSelect kind="roster" label="Manager" value={draft.managerSyscode} onChange={(v) => set('managerSyscode', v)} />
          <GenderSelect value={draft.genderRef} onChange={(v) => set('genderRef', v)} />
        </div>
      </fieldset>

      <fieldset className="space-y-3">
        <legend className="text-xs font-semibold uppercase tracking-wider text-fg-dim">
          Classification
        </legend>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <PicklistSelect group="DIVISION" label="Division" value={draft.division} onChange={(v) => set('division', v)} />
          <PicklistSelect group="EMPLOY_CAT" label="Category of employee" value={draft.employeeCategory} onChange={(v) => set('employeeCategory', v)} />
          <PicklistSelect group="PRIM_TRADE_ROLE" label="Primary trade / role" value={draft.primaryTradeRole} onChange={(v) => set('primaryTradeRole', v)} />
          <PicklistSelect group="REPORTINGTEAMS" label="Reporting team" value={draft.reportingTeam} onChange={(v) => set('reportingTeam', v)} />
          <PicklistSelect group="DEPT_ACC" label="Department / account" value={draft.departmentAccount} onChange={(v) => set('departmentAccount', v)} />
          <PicklistSelect group="PL_COST_TYPE" label="Cost type" value={draft.costType} onChange={(v) => set('costType', v)} />
          <PicklistSelect group="APL_COL" label="Worker type" value={draft.workerType} onChange={(v) => set('workerType', v)} />
          <PicklistSelect group="APL_CAT" label="Worker category" value={draft.workerCategory} onChange={(v) => set('workerCategory', v)} />
        </div>
      </fieldset>

      <fieldset className="space-y-3">
        <legend className="text-xs font-semibold uppercase tracking-wider text-fg-dim">
          HCC &amp; contact
        </legend>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <ZoneSelect label="HCC primary zone" value={draft.primaryZone} onChange={(v) => set('primaryZone', v)} />
          <ZoneSelect label="HCC secondary zone" value={draft.secondaryZone} onChange={(v) => set('secondaryZone', v)} />
          <label className="block">
            <span className={FIELD_LABEL_CLS}>Mobile phone</span>
            <input
              className={FIELD_INPUT_CLS}
              value={draft.mobilePhone ?? ''}
              onChange={(e) => set('mobilePhone', e.target.value || null)}
            />
          </label>
          <div className="sm:col-span-2">
            <span className={FIELD_LABEL_CLS}>Person type</span>
            <div className="mt-1">
              <MultiSelect
                groups={[
                  {
                    options: PLANON_PERSON_TYPE_OPTIONS.map((o) => ({
                      value: String(o.code),
                      label: o.label,
                    })),
                  },
                ]}
                value={draft.personTypes.map(String)}
                onChange={(next) => set('personTypes', next.map(Number))}
                placeholder="Choose person type(s)…"
                ariaLabel="Person type"
              />
            </div>
          </div>
        </div>
      </fieldset>
    </div>
  );
}

function GenderSelect({ value, onChange }: { value: number | null; onChange: (v: number | null) => void }) {
  return (
    <label className="block">
      <span className={FIELD_LABEL_CLS}>Gender</span>
      <select
        className={FIELD_INPUT_CLS}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
      >
        <option value="">Choose…</option>
        {PLANON_GENDER_OPTIONS.map((o) => (
          <option key={o.syscode} value={o.syscode}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function ZoneSelect({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string | null;
  onChange: (v: string | null) => void;
}) {
  return (
    <label className="block">
      <span className={FIELD_LABEL_CLS}>{label}</span>
      <select
        className={FIELD_INPUT_CLS}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
      >
        <option value="">Choose…</option>
        {PLANON_ZONE_OPTIONS.map((z) => (
          <option key={z} value={z}>
            {z}
          </option>
        ))}
      </select>
    </label>
  );
}
